import type {
  PostFlightOpportunity,
  PostFlightScoreResult,
} from "@/features/post-flight/api/post-flight.api";

/** Lead row order. A lower rank wins inside the same type. */
const LEAD_TYPE_ORDER = [
  "winning_creative",
  "underexplored_winner",
  "emerging_creative",
  "strong_hook",
] as const;

export const POST_FLIGHT_WINDOWS = [14, 30, 90] as const;
export type PostFlightWindow = (typeof POST_FLIGHT_WINDOWS)[number];

export interface PostFlightSearch {
  window: PostFlightWindow;
  ad?: string;
}

export interface PostFlightAd {
  hash: string;
  rows: PostFlightOpportunity[];
  /** Levers that did not fit on a real opportunity. The card and drawer do not render these. */
  carriedLevers?: readonly string[];
}

export function parsePostFlightSearch(search: Record<string, unknown>): PostFlightSearch {
  const window = POST_FLIGHT_WINDOWS.find((value) => String(value) === String(search.window)) ?? 90;
  const ad = typeof search.ad === "string" && search.ad.length > 0 ? search.ad : undefined;
  return ad === undefined ? { window } : { window, ad };
}

/** A window change closes the open ad. */
export function searchForWindow(window: PostFlightWindow): PostFlightSearch {
  return { window };
}

export function qualifyingAds(
  rows: readonly PostFlightOpportunity[],
  windowDays: PostFlightWindow,
): PostFlightAd[] {
  const grouped = new Map<string, PostFlightOpportunity[]>();
  for (const row of rows) {
    if (row.window_days !== windowDays || row.status !== "qualifies") continue;
    const current = grouped.get(row.hash) ?? [];
    current.push(row);
    grouped.set(row.hash, current);
  }
  return [...grouped.values()].map((group) => ({
    hash: group[0]?.hash ?? "",
    rows: sortLeadFirst(group),
  }));
}

export const POST_FLIGHT_AD_TYPES = ["video", "image"] as const;
export type PostFlightAdType = (typeof POST_FLIGHT_AD_TYPES)[number];

/** Image when the creative URL or object type says so. Everything else is video. */
export function creativeKind(ad: PostFlightAd): PostFlightAdType {
  const mediaRow = ad.rows.find((row) => row.video_url) ?? ad.rows[0];
  return isImageCreative(mediaRow?.video_url, mediaRow?.object_type) ? "image" : "video";
}

/** An empty ad-type list means every creative. Kind follows the rows that remain after the other filters. */
export function adsMatchingFilters(
  ads: readonly PostFlightAd[],
  types: readonly string[],
  platform: string,
  adTypes: readonly string[],
): PostFlightAd[] {
  return ads.flatMap((ad) => {
    const rows = ad.rows.filter(
      (row) =>
        (types.length === 0 || types.includes(row.opportunity_type)) &&
        (platform === "" || row.source.toLowerCase() === platform.toLowerCase()),
    );
    if (rows.length === 0) return [];
    const carried = carryServiceScores(ad.rows, sortLeadFirst(rows));
    const filtered = {
      hash: ad.hash,
      rows: carried.rows,
      ...(carried.carriedLevers.length > 0 ? { carriedLevers: carried.carriedLevers } : {}),
    };
    if (adTypes.length > 0 && !adTypes.includes(creativeKind(filtered))) return [];
    return [filtered];
  });
}

/** An unknown hash stays closed. A pending load is not treated as unknown yet. */
export function openAdHash(
  ads: readonly PostFlightAd[],
  adHash: string | undefined,
  isLoaded: boolean,
): string | undefined {
  if (!isLoaded || !adHash) return undefined;
  return ads.some((ad) => ad.hash === adHash) ? adHash : undefined;
}

const LEVER_SLOTS = ["lever_1", "lever_2", "lever_3"] as const;

interface CarriedRows {
  rows: PostFlightOpportunity[];
  carriedLevers: string[];
}

/** A type filter can drop the scored lead. Scores stay on the same platform. */
function carryServiceScores(
  original: readonly PostFlightOpportunity[],
  kept: PostFlightOpportunity[],
): CarriedRows {
  if (kept.some((row) => serviceResults(row))) return { rows: kept, carriedLevers: [] };
  const lead = kept[0];
  if (!lead) return { rows: kept, carriedLevers: [] };
  // The service scores each opportunity row. The carried recommendation is the
  // page lead among those rows: type order, then the lower rank. Payload order is not that lead.
  const scored = sortLeadFirst(original.filter((row) => serviceResults(row) && sameSource(row, lead)))[0];
  if (!scored) return { rows: kept, carriedLevers: [] };
  return stampServiceScores(scored, kept);
}

function sameSource(left: PostFlightOpportunity, right: PostFlightOpportunity): boolean {
  return left.source.toLowerCase() === right.source.toLowerCase();
}

function stampServiceScores(
  scored: PostFlightOpportunity,
  kept: readonly PostFlightOpportunity[],
): CarriedRows {
  const selected = leverTexts(scored)[0];
  const merged = uniqueLevers([
    ...(selected ? [selected] : []),
    ...kept.flatMap((row) => leverTexts(row)),
    ...leverTexts(scored),
  ]);
  const capacity = kept.length * LEVER_SLOTS.length;
  const rows = kept.map((row, index) => {
    const chunk = merged.slice(index * LEVER_SLOTS.length, (index + 1) * LEVER_SLOTS.length);
    const filled = {
      ...row,
      lever_1: chunk[0] ?? null,
      lever_2: chunk[1] ?? null,
      lever_3: chunk[2] ?? null,
    };
    if (index !== 0) return filled;
    return {
      ...filled,
      hook_score: scored.hook_score,
      hold_score: scored.hold_score,
      hook_result: scored.hook_result,
      hold_result: scored.hold_result,
    };
  });
  return { rows, carriedLevers: merged.slice(capacity) };
}

function uniqueLevers(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const levers: string[] = [];
  for (const value of values) {
    if (seen.has(value)) continue;
    seen.add(value);
    levers.push(value);
  }
  return levers;
}

function leverTexts(row: PostFlightOpportunity): string[] {
  return LEVER_SLOTS.flatMap((key) => {
    const text = row[key]?.trim();
    return text ? [text] : [];
  });
}

function sortLeadFirst(rows: PostFlightOpportunity[]): PostFlightOpportunity[] {
  return [...rows].sort((left, right) => {
    const typeDelta = typeRank(left.opportunity_type) - typeRank(right.opportunity_type);
    if (typeDelta !== 0) return typeDelta;
    return (left.rank ?? Number.POSITIVE_INFINITY) - (right.rank ?? Number.POSITIVE_INFINITY);
  });
}

function typeRank(type: string): number {
  const index = LEAD_TYPE_ORDER.indexOf(type as (typeof LEAD_TYPE_ORDER)[number]);
  return index === -1 ? LEAD_TYPE_ORDER.length : index;
}

const LEVER_PATTERN = /^(\d+)\s+(.+?)\s+\(([^)]+)\)\s*$/;

function titled(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export interface PostFlightLever {
  id: string;
  name: string;
  effort: string;
}

/** How hook and hold compare with the account. Null when either ratio is missing. */
export type HookHoldRead = "keep-hook" | "new-hook" | "repeat" | "rebuild";

export function hookHoldRead(hook: number | null, hold: number | null): HookHoldRead | null {
  if (hook === null || hold === null) return null;
  const hookStrong = hook >= 1;
  const holdStrong = hold >= 1;
  if (hookStrong && holdStrong) return "repeat";
  if (hookStrong && !holdStrong) return "keep-hook";
  if (holdStrong && !hookStrong) return "new-hook";
  return "rebuild";
}

export const DOUBLE_DOWN_SCRIPT_COUNTS = [1, 2, 3, 4, 5] as const;
export const DEFAULT_SCRIPT_COUNT = 2;

/** Catalog copy from the Phase 1 design. Unknown ids fall back to the parsed lever. */
const DOUBLE_DOWN_LEVER_CATALOG: Readonly<Record<string, { title: string; detail: string }>> = {
  "1": { title: "Diversification of Products", detail: "Keep the script and change the product color/type" },
  "2": {
    title: "Stitch Hook with a different Ad Body",
    detail: "Keep the same hook and stitch it to the most relevant ad body with the best conversion rate (re-write the stitch if needed)",
  },
  "3": { title: "Double down on speed", detail: "Keep the same script but change the speed of the ad (10% faster)" },
  "4": { title: "Double down on concept", detail: "Take a winning script and change the concept to an underindexed concept" },
  "5": { title: "Double down on language", detail: "Keep the same script but change the language voiceover" },
  "6": { title: "DD Voiceover", detail: "Keep the same script but change the accent voiceover" },
  "7": { title: "Voiceover to Song", detail: "Take the voiceover and produce music from it" },
  "8": { title: "DD script", detail: "Keep the script VO but with different creators" },
  "9": { title: "DD Sound", detail: "Keep the best performing footage and change the soundtrack" },
  "10": { title: "Diversification of a CTA", detail: "Keep the same script but make the CTA more prominent in multiple ways" },
  "11": { title: "DD presenter", detail: "Keep the same presenter but come up with new scripts" },
  "12": { title: "Diversification of age", detail: "Keep the same script but change the age of the presenter" },
  "13": {
    title: "Human for animal",
    detail: "Keep the same script but change the human to an animal or mascot (AI UGC or animation)",
  },
  "14": {
    title: "DD Localization",
    detail: "change the localization in the ad (visual mainly i.e background, landscape, weather, etc)",
  },
  "15": {
    title: "Diversification of execution",
    detail: "Keep the same script but change the visual treatment and shooting style (animation, cinematic, etc.)",
  },
  "16": {
    title: "Diversification of ratio",
    detail: "Keep the same script but change the ratio to all relevant specs to all platforms (youtube, meta, tiktok)",
  },
  "17": {
    title: "Diversification of content genre",
    detail: "Keep the same script but change the genre (comedy, drama, suspense, etc.)",
  },
  "18": {
    title: "Diversification of length",
    detail: "Keep the same script but change the length of the video and add more storytelling components and scenes",
  },
  "19": { title: "Diversification of surprise element", detail: "Keep the same script but change the surprise element in the ad" },
  "20": {
    title: "Double down on shooting angle",
    detail: "Keep the same script but change the camera angle from 3rd person into POV (i.e shakier camera, more close ups, etc.)",
  },
};

export type DoubleDownPrimaryRead =
  | "new-hook"
  | "new-body"
  | "new-presenter"
  | "shorter-body"
  | "hook-above"
  | "hook-below"
  | "hook-even"
  | "hold-above"
  | "hold-below"
  | "hold-even"
  | "both-even";

export interface DoubleDownPrimary {
  read: DoubleDownPrimaryRead;
  hook: string;
  hookBase: string;
  hold: string;
  hookX: string;
  holdX: string;
  prompt: string;
}

export interface DoubleDownExtra {
  id: string;
  title: string;
  name: string;
  detail: string;
  effort: string;
  prompt: string;
  isBestFit?: boolean;
  hookResult?: PostFlightScoreResult | null;
  holdResult?: PostFlightScoreResult | null;
  hookX?: string;
  holdX?: string;
}

export interface DoubleDownPlan {
  primary: DoubleDownPrimary | null;
  extras: DoubleDownExtra[];
}

export function leverCatalog(id: string): { title: string; detail: string } | null {
  return DOUBLE_DOWN_LEVER_CATALOG[id] ?? null;
}

const PRIMARY_READ: Record<HookHoldRead, DoubleDownPrimaryRead> = {
  "keep-hook": "new-body",
  "new-hook": "new-hook",
  repeat: "new-presenter",
  rebuild: "shorter-body",
};

/**
 * Every lever the service sent is a tile. When hook and hold results are present,
 * those results choose the best fit, so the page does not invent a second recommendation.
 */
export function doubleDownPlan(ad: PostFlightAd): DoubleDownPlan {
  const scored = ad.rows.find((row) => serviceResults(row));
  const results = serviceResults(scored);
  const levers = parseLevers(ad.rows, ad.carriedLevers ?? []);
  const selectedId = results ? (serviceLeverId(scored) ?? levers[0]?.id) : undefined;
  const extras = levers.map((lever) => toExtra(lever, results && lever.id === selectedId ? scored : undefined));
  if (results) return { primary: null, extras };
  const lead = ad.rows[0];
  const hookRate = classifiedRatio(lead?.thumbstop, lead?.thumbstop_expected);
  const holdRate = classifiedRatio(lead?.hold_rate, lead?.account_hold_rate);
  const read = primaryRead(hookRate, holdRate);
  const primary = read === null ? null : primaryTile(lead, hookRate, holdRate, read);
  return { primary, extras };
}

function serviceLeverId(row: PostFlightOpportunity | undefined): string | undefined {
  return row ? parseLevers([row])[0]?.id : undefined;
}

function serviceResults(
  lead: PostFlightOpportunity | undefined,
): { hook: PostFlightScoreResult; hold: PostFlightScoreResult } | null {
  const hook = lead?.hook_result;
  const hold = lead?.hold_result;
  if (hook !== "weak" && hook !== "ok") return null;
  if (hold !== "weak" && hold !== "ok") return null;
  return { hook, hold };
}

function finiteScore(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toExtra(lever: PostFlightLever, scored: PostFlightOpportunity | undefined): DoubleDownExtra {
  const catalog = leverCatalog(lever.id);
  const extra: DoubleDownExtra = {
    id: lever.id,
    title: catalog?.title ?? lever.name,
    name: lever.name,
    detail: catalog?.detail ?? lever.effort,
    effort: lever.effort,
    prompt: catalog?.detail ?? lever.name,
    isBestFit: scored !== undefined,
  };
  if (!scored) return extra;
  return {
    ...extra,
    hookResult: scored.hook_result,
    holdResult: scored.hold_result,
    hookX: formatMultiple(finiteScore(scored.hook_score)),
    holdX: formatMultiple(finiteScore(scored.hold_score)),
  };
}

type RateBand = "above" | "below" | "even";

interface ClassifiedRate {
  ratio: number;
  band: RateBand;
}

// A missing ratio is unknown, so it does not pick a two-metric recommendation.
// An even band is a displayed tie, so it is not above and not below.
function primaryRead(hook: ClassifiedRate | null, hold: ClassifiedRate | null): DoubleDownPrimaryRead | null {
  if (hook && !hold) return oneSidedRead("hook", hook.band);
  if (hold && !hook) return oneSidedRead("hold", hold.band);
  if (!hook || !hold) return null;
  if (hook.band === "even" && hold.band === "even") return "both-even";
  if (hook.band === "even") return oneSidedRead("hold", hold.band);
  if (hold.band === "even") return oneSidedRead("hook", hook.band);
  const compared = hookHoldRead(hook.ratio, hold.ratio);
  if (compared === null) return null;
  return PRIMARY_READ[compared];
}

function oneSidedRead(side: "hook" | "hold", band: RateBand): DoubleDownPrimaryRead {
  if (band === "even") return side === "hook" ? "hook-even" : "hold-even";
  if (side === "hook") return band === "above" ? "hook-above" : "hook-below";
  return band === "above" ? "hold-above" : "hold-below";
}

type PartialRead = "hook-above" | "hook-below" | "hook-even" | "hold-above" | "hold-below" | "hold-even";

function partialPrompt(read: PartialRead): string {
  if (read === "hook-above") {
    return "The opening stops the scroll more than the account average. Keep that opening.";
  }
  if (read === "hook-below") {
    return "The opening stops the scroll less than the account average. Write a new first line, first frame, and on-screen text.";
  }
  if (read === "hook-even") {
    return "The opening matches the account average.";
  }
  if (read === "hold-above") {
    return "People keep watching longer than the account average. Keep the rest of the ad.";
  }
  if (read === "hold-even") {
    return "Watch time matches the account average.";
  }
  return "People leave sooner than the account average. Rewrite what follows the opening so they stay.";
}

function primaryTile(
  lead: PostFlightOpportunity | undefined,
  hookRate: ClassifiedRate | null,
  holdRate: ClassifiedRate | null,
  read: DoubleDownPrimaryRead,
): DoubleDownPrimary {
  const hookShown = shownRate(lead?.thumbstop, lead?.thumbstop_expected);
  const holdShown = shownRate(lead?.hold_rate, lead?.account_hold_rate);
  const stats = {
    hook: hookShown.value,
    hookBase: hookShown.baseline,
    hold: `${holdShown.value} hold vs ${holdShown.baseline}`,
    hookX: formatMultiple(hookRate?.ratio ?? null),
    holdX: formatMultiple(holdRate?.ratio ?? null),
  };
  if (read === "both-even") {
    return { read, ...stats, prompt: "Hook and hold both match the account average." };
  }
  if (
    read === "hook-above" ||
    read === "hook-below" ||
    read === "hook-even" ||
    read === "hold-above" ||
    read === "hold-below" ||
    read === "hold-even"
  ) {
    return { read, ...stats, prompt: partialPrompt(read) };
  }
  if (read === "new-hook") {
    return {
      read,
      ...stats,
      prompt: "Keep the ad body and write new hooks: a new first line, first frame and on-screen text that stop the scroll faster.",
    };
  }
  if (read === "new-body") {
    return {
      read,
      ...stats,
      prompt: leverCatalog("2")?.detail ?? "",
    };
  }
  if (read === "new-presenter") {
    return {
      read,
      ...stats,
      prompt: leverCatalog("8")?.detail ?? "",
    };
  }
  return {
    read: "shorter-body",
    ...stats,
    prompt: "Keep the offer and CTA. Write a new hook and cut the body so the product appears in the first 5 seconds.",
  };
}

/** A displayed tie is its own band, so it is neither above nor below. */
function classifiedRatio(value: number | null | undefined, baseline: number | null | undefined): ClassifiedRate | null {
  const compared = ratio(value, baseline);
  if (compared === null || typeof value !== "number" || typeof baseline !== "number") return null;
  const band: RateBand = isEvenRate(value, baseline) ? "even" : compared >= 1 ? "above" : "below";
  return { ratio: compared, band };
}

/** Missing or zero baseline → no ratio. */
export function ratio(value: number | null | undefined, denominator: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  if (typeof denominator !== "number" || !Number.isFinite(denominator) || denominator === 0) return null;
  return value / denominator;
}

export function shortHash(hash: string): string {
  return hash.slice(0, 6);
}

export function isRunning(active: boolean | number | null | undefined): boolean | null {
  if (active === undefined || active === null) return null;
  if (typeof active === "number") return active !== 0;
  return active;
}

/** Design scale is 2.5×. The account average (1×) sits at 40%. */
export const HOOK_HOLD_SCALE = 2.5;

export function barWidth(value: number | null): number {
  if (value === null) return 0;
  return Math.min(100, (value / HOOK_HOLD_SCALE) * 100);
}

export function formatMultiple(value: number | null): string {
  if (value === null) return "—";
  return `${value < 0.1 ? value.toFixed(2) : value.toFixed(1)}×`;
}

/** Service rates are fractions. 1.2 is 120%, including values above 1. */
export function formatPercent(value: number, digits = 0): string {
  return `${(value * 100).toFixed(digits)}%`;
}

const RATE_PAIR_DIGITS = [0, 1, 2] as const;

/**
 * Distinct values that round to the same percent use one more decimal, up to two.
 * Two decimals that still match are an even rate, so a chip does not read as below.
 */
export function formatRatePair(value: number, baseline: number): { value: string; baseline: string } {
  for (const digits of RATE_PAIR_DIGITS) {
    const pair = { value: formatPercent(value, digits), baseline: formatPercent(baseline, digits) };
    if (pair.value !== pair.baseline || value === baseline) return pair;
  }
  return { value: formatPercent(value, 2), baseline: formatPercent(baseline, 2) };
}

/** True when the two rates print as the same percent, including an exact match. */
export function isEvenRate(value: number, baseline: number): boolean {
  const pair = formatRatePair(value, baseline);
  return pair.value === pair.baseline;
}

function shownRate(
  value: number | null | undefined,
  baseline: number | null | undefined,
): { value: string; baseline: string } {
  if (typeof value === "number" && typeof baseline === "number") return formatRatePair(value, baseline);
  return {
    value: typeof value === "number" ? formatPercent(value) : "—",
    baseline: typeof baseline === "number" ? formatPercent(baseline) : "—",
  };
}

export function toPercentPoints(value: number): number {
  return Math.round(value * 100);
}

const IMAGE_URL = /\.(png|jpe?g|gif|webp|avif)$/i;

/** A png URL is an image even when the row is labelled Video. */
export function isImageCreative(
  url: string | null | undefined,
  objectType?: string | null,
): boolean {
  const path = url?.split(/[?#]/)[0] ?? "";
  if (IMAGE_URL.test(path)) return true;
  return objectType?.trim().toLowerCase() === "image";
}

export function prettyLabel(value: string | null | undefined): string {
  if (!value) return "";
  const text = value.trim().replace(/_\d+$/, "").replace(/_/g, " ");
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function evidenceLabel(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return "";
  const titled = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /evidence/i.test(titled) ? titled : `${titled} evidence`;
}

export function cleanReason(value: string | null | undefined): string {
  if (!value) return "";
  return value.replace(/'([a-z0-9_]+)'/gi, (_match, token: string) => prettyLabel(token));
}

export function formatSpend(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1000) {
    const compact = value / 1000;
    const digits = Math.abs(compact) >= 10 ? 0 : 1;
    return `${compact.toFixed(digits)}K`;
  }
  return new Intl.NumberFormat("en", { maximumFractionDigits: 0 }).format(value);
}

/** Spend share is a fraction, same contract as {@link formatPercent}. */
export function formatShare(value: number): string | null {
  if (!Number.isFinite(value)) return null;
  return `${(value * 100).toFixed(1)}%`;
}

const POST_FLIGHT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
};

const POST_FLIGHT_DATE_FORMAT_WITH_YEAR: Intl.DateTimeFormatOptions = {
  ...POST_FLIGHT_DATE_FORMAT,
  year: "numeric",
};

/** ISO calendar date (`YYYY-MM-DD`) or full timestamp for display. */
export function formatPostFlightDateLabel(value: string | null | undefined, withYear = false): string | null {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return null;
  const parsed = new Date(trimmed.includes("T") ? trimmed : `${trimmed}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat("en", withYear ? POST_FLIGHT_DATE_FORMAT_WITH_YEAR : POST_FLIGHT_DATE_FORMAT).format(
    parsed,
  );
}

/** Inclusive window: `start = as_of − (window_days − 1)`. */
export function formatWindowRange(asOf: string | null, windowDays: number): string | null {
  if (!asOf || windowDays < 1) return null;
  const end = new Date(asOf.includes("T") ? asOf : `${asOf}T00:00:00.000Z`);
  if (Number.isNaN(end.getTime())) return null;
  const start = new Date(end);
  start.setUTCDate(start.getUTCDate() - (windowDays - 1));
  const startText = new Intl.DateTimeFormat("en", POST_FLIGHT_DATE_FORMAT).format(start);
  const endText = new Intl.DateTimeFormat("en", POST_FLIGHT_DATE_FORMAT_WITH_YEAR).format(end);
  return `${startText} – ${endText}`;
}

export function formatWindowRangeFromBounds(
  windowStart: string | null | undefined,
  windowEnd: string | null | undefined,
): string | null {
  const startText = formatPostFlightDateLabel(windowStart, false);
  const endText = formatPostFlightDateLabel(windowEnd, true);
  if (!startText || !endText) return null;
  return `${startText} – ${endText}`;
}

/** Prefer service `window_start`/`window_end`; fall back to inclusive `as_of` math. */
export function resolveWindowRangeLabel(
  row: Pick<PostFlightOpportunity, "window_start" | "window_end"> | null | undefined,
  asOf: string | null,
  windowDays: number,
): string | null {
  const fromBounds = formatWindowRangeFromBounds(row?.window_start, row?.window_end);
  if (fromBounds) return fromBounds;
  return formatWindowRange(asOf, windowDays);
}

/**
 * List header range: every qualifying ad lead must share the same valid bounds, otherwise
 * fall back to inclusive `as_of` math for the selected window.
 */
export function resolveQualifyingListWindowRangeLabel(
  ads: readonly PostFlightAd[],
  asOf: string | null,
  windowDays: number,
): string | null {
  const leadRows = ads.flatMap((ad) => (ad.rows[0] ? [ad.rows[0]] : []));
  if (leadRows.length === 0) return formatWindowRange(asOf, windowDays);

  let sharedBounds: string | null = null;
  let everyLeadHasMatchingBounds = true;

  for (const row of leadRows) {
    const fromBounds = formatWindowRangeFromBounds(row.window_start, row.window_end);
    if (!fromBounds) {
      everyLeadHasMatchingBounds = false;
      break;
    }
    if (sharedBounds === null) sharedBounds = fromBounds;
    else if (sharedBounds !== fromBounds) {
      everyLeadHasMatchingBounds = false;
      break;
    }
  }

  if (everyLeadHasMatchingBounds && sharedBounds) return sharedBounds;
  return formatWindowRange(asOf, windowDays);
}

export function adTitle(ad: PostFlightAd): string {
  const concept = prettyLabel(ad.rows[0]?.primary_concept);
  const hash = shortHash(ad.hash);
  return concept ? `${concept} · ${hash}` : hash;
}

/** A numbered lever keeps its id. A plain title such as "New hook, same body" is kept as-is. */
export function parseLevers(
  rows: readonly PostFlightOpportunity[],
  carriedLevers: readonly string[] = [],
): PostFlightLever[] {
  const seen = new Set<string>();
  const levers: PostFlightLever[] = [];
  const texts = [
    ...rows.flatMap((row) => [row.lever_1, row.lever_2, row.lever_3]),
    ...carriedLevers,
  ];
  for (const raw of texts) {
    const text = raw?.trim();
    if (!text) continue;
    const match = LEVER_PATTERN.exec(text);
    const id = match ? (match[1] ?? "") : plainLeverId(text);
    const parsedName = match ? (match[2] ?? "") : text;
    const effort = match ? titled(match[3] ?? "") : "";
    if (!id || seen.has(id)) continue;
    seen.add(id);
    levers.push({ id, name: parsedName, effort });
  }
  return levers;
}

function plainLeverId(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const id = slug || "lever";
  return /^\d+$/.test(id) ? `plain-${id}` : id;
}
